import React, { JSX, useState } from 'react';
import { isEmpty } from 'lodash';
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import { lzFMaskify } from '@lz-services/String';
import { LzTColors } from '@lz-types/types';

import './InputField.scss';

export interface LzPInput {
  fluid?: boolean;
  horizontal?: boolean;
  required?: boolean;
  type?: string;
  autoFocus?: boolean;
  placeholder?: string;
  label?: string;
  insideLabelStart?: string;
  insideLabelStartGlued?: boolean; // no margin between the inside label and the input text (by default it has some space between)
  mask?: string;
  name?: string;
  icon?: JSX.Element;
  id?: string;
  className?: string;
  style?: React.CSSProperties;
  formErrors?: any;
  value?: string;
  hintText?: string;
  disabled?: boolean;
  readonly?: boolean;
  multiline?: boolean;
  labelShrinked?: boolean;
  compact?: boolean;
  noAutocomplete?: boolean;
  size?: 'small' | 'medium';
  color?: LzTColors;
  variant?: 'outlined' | 'filled' | 'standard';
  onChange?: (name: string, value: string) => void;
  onKeyUp?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  maxCharsLimit?: number,
  showCharsStatus?: false,
  ref?: React.Ref<HTMLInputElement>,
}

const LzXInputField = (props: LzPInput) => {
  const isPasswordField = 'password' === props.type;
  const [readonlyState, setReadonlyState] = useState(isPasswordField);

  const theName = props.name || '';
  const theId = props.id || 'id_' + theName;
  const inputProps: {
    startAdornment?: JSX.Element;
  } = {};
  if (props.insideLabelStart) {
    inputProps.startAdornment = <InputAdornment position="start" sx={{ marginRight: props.insideLabelStartGlued ? -0.9 : undefined }}>{props.insideLabelStart}</InputAdornment>;
  }
  const htmlInputProps: {
    ref?: React.Ref<HTMLInputElement>;
    min?: number;
    max?: number;
    step?: number;
    maxlength?: number;
    placeholder?: string;
    readOnly?: boolean;
    onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
    onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  } = {};
  htmlInputProps.ref = props.ref;
  var theType = props.type || 'text';
  if ('year' === theType) {
    theType = 'number';
    htmlInputProps.min = 1900;
    htmlInputProps.max = 2099;
    htmlInputProps.step = 1;
    htmlInputProps.placeholder = 'YYYY';
  }
  const hasMaxCharsLimit = typeof props.maxCharsLimit === 'number' && props.maxCharsLimit > 0;
  if (hasMaxCharsLimit) {
    htmlInputProps.maxlength = props.maxCharsLimit;
  }
  // This below is done because Chrome does not follow the autocomplete instructions and this below is a hack arround it
  // see this stackoverflow answer: https://stackoverflow.com/a/26931463
  if (isPasswordField) {
    htmlInputProps.readOnly = !!props.readonly || readonlyState;
    htmlInputProps.onFocus = () => setReadonlyState(false);
    htmlInputProps.onBlur = () => setReadonlyState(true);
  }

  const autoComplete =
    props.noAutocomplete || isPasswordField ? (isPasswordField ? 'dont-show' : 'off') : 'on';

  const labelShrinked = props.labelShrinked || 'date' === theType || 'time' === theType;

  const hasError = props.formErrors && props.formErrors[theName];

  let charsStatus = undefined;
  if (props.showCharsStatus) {
    const vaLen = props.value?.length || 0;
    const fullPerc = (vaLen / (hasMaxCharsLimit ? props.maxCharsLimit || 1 : 0)) * 100;
    const statColor = hasMaxCharsLimit ? (fullPerc < 70 ? 'black' : (fullPerc > 90 ? '#da0101' : 'maroon')) : 'black';
    charsStatus = <span style={{ display: 'block', textAlign: 'right', color: statColor }}>{vaLen + (hasMaxCharsLimit ? '/' + props.maxCharsLimit : '') + ' chars'}</span>;
  }

  const handleOnChange = (val: string): void => {
    let theVal = val;
    if (typeof props.mask !== 'undefined' && props.mask !== '') {
      // console.log('REGEXP', theVal, props.mask, theVal.replace(props.mask, ''));
      theVal = lzFMaskify(theVal, props.mask);
    }
    if (props.onChange) {
      props.onChange(theName, theVal);
    }
  };

  if (props.horizontal) {
    return (
      <Grid container spacing={2} className={props.className} sx={{ marginBottom: 1 }}>
        <Grid size={{ sm: 4 }}><Typography variant="overline">{props.label} :</Typography></Grid>
        <Grid size={{ sm: 8 }}>
          <TextField
            id={theId}
            name={theName}
            type={theType}
            fullWidth={props.fluid}
            required={props.required}
            autoFocus={props.autoFocus}
            disabled={props.disabled}
            autoComplete={autoComplete}
            placeholder={props.placeholder || props.mask}
            multiline={props.multiline}
            className={
              'lz-base-field lz-input-field ' +
              (props.compact ? 'lz-compact ' : '') +
              (props.className || '')
            }
            style={props.style}
            size={props.size || 'small'}
            color={props.color}
            variant={props.variant || 'standard'}
            onFocus={event => { event.target.select(); }}
            slotProps={{
              input: inputProps,
              htmlInput: htmlInputProps
            }}
            value={props.value}
            onChange={(evt) => handleOnChange(evt.target.value)}
            onKeyUp={props.onKeyUp}
            error={props.formErrors && props.formErrors[theName]}
          />
        </Grid>
      </Grid>
    );
  }

  return (
    <TextField
      id={theId}
      name={theName}
      type={theType}
      fullWidth={props.fluid}
      required={props.required}
      autoFocus={props.autoFocus}
      autoComplete={autoComplete}
      placeholder={props.placeholder || props.mask}
      label={props.label}
      helperText={
        hasError
          ? props.formErrors[theName]
          : props.hintText || props.placeholder || props.mask || charsStatus || ''
      }
      value={props.value}
      slotProps={{
        input: inputProps,
        htmlInput: htmlInputProps,
        inputLabel: labelShrinked || !isEmpty(props.value) ? { shrink: true } : {},
      }}
      className={
        'lz-base-field lz-input-field ' +
        (props.compact ? 'lz-compact ' : '') +
        (props.className || '')
      }
      style={props.style}
      size={props.size || 'small'}
      color={props.color}
      variant={props.variant || 'standard'}
      onFocus={event => { event.target.select(); }}
      onChange={(evt) => handleOnChange(evt.target.value)}
      onKeyUp={props.onKeyUp}
      error={props.formErrors && props.formErrors[theName]}
      disabled={props.disabled}
      multiline={props.multiline}
    />
  );
};

export default LzXInputField;
